import LoadingDots from 'layout/widgets/LoadingDots'
import { ArrowLeftSmIcon, ArrowRightSmIcon, DownloadIcon, IconButton, styled } from 'opensolar-ui'
import React, { useEffect, useState } from 'react'
import { useTranslate } from 'react-admin'
import { PrivateFileType } from 'types/privateFile'
import { downloadFile } from 'util/download'

const PdfRendererWrapper = styled('div')<{ scrollable?: boolean }>(({ theme, scrollable }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  position: 'relative',
  overflow: scrollable ? 'auto' : 'hidden',
  overflowX: 'hidden',
  height: scrollable ? '60vh' : 'auto',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    height: scrollable ? '70vh' : 'auto',
  },
}))

const Canvas = styled('canvas')(({ theme }) => ({
  maxHeight: '100%',
  maxWidth: '100%',
  height: 'auto',
  width: 'auto',
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    height: 'auto',
  },
}))

const ScrollableCanvas = styled('canvas')(({ theme }) => ({
  maxWidth: '100%',
  marginBottom: theme.spacing(1.5),
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  height: 'auto',
  width: 'auto',
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    marginBottom: theme.spacing(1),
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
}))

const PdfPagesContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  overflowX: 'hidden',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
  },
}))

const PageNavbar = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1),
  bottom: 0,
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    fontSize: '0.875rem',
  },
}))

const DownloadButtonContainer = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  right: theme.spacing(2),
  alignSelf: 'flex-end',
  zIndex: 10,
  marginLeft: 'auto',
}))

type PropTypes = {
  pdfUrl: string
  scrollable?: boolean
  quality?: number
  allowDownload?: boolean
}

export const PdfRenderer: React.FunctionComponent<PropTypes> = (props) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [docLoading, setDocLoading] = useState<boolean>(false)
  const [libraryLoading, setLibraryLoading] = useState<boolean>(false)

  const [pdfObj, setPdfObj] = useState<any>(undefined)
  const [pageNum, setPageNum] = useState<number>(0)
  const [pageCount, setPageCount] = useState<number | undefined>(undefined)
  const [renderedPages, setRenderedPages] = useState<number>(0)
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth)

  const translate = useTranslate()
  const { scrollable = false, allowDownload = false } = props

  useEffect(() => {
    let script: HTMLScriptElement | null = null
    const loadPdfJs = () => {
      setLibraryLoading(true)
      const minUrl = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.7.570/build/pdf'
      script = document.createElement('script')
      script.addEventListener('load', () => {
        window['pdfjsLib'] = window['pdfjs-dist/build/pdf']
        window['pdfjsLib'].GlobalWorkerOptions.workerSrc = `${minUrl}.worker.min.js`
        setLibraryLoading(false)
      })
      script.addEventListener('error', () => {
        console.error('Failed to load pdf.js library')
        setLibraryLoading(false)
      })
      script.src = `${minUrl}.min.js`
      script.id = 'temp-target'
      document.head.appendChild(script)
    }
    if (!window['pdfjsLib']) {
      loadPdfJs()
    } else {
      setLibraryLoading(false)
    }
    return () => {
      if (script) {
        script.remove()
      }
    }
  }, [])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    setLoading(libraryLoading || docLoading)
  }, [libraryLoading, docLoading])

  useEffect(() => {
    let didCancel = false
    if (!libraryLoading && props.pdfUrl) {
      setDocLoading(true)
      const checkPdfJsLib = () => {
        const pdfjsLib = window['pdfjsLib']
        if (pdfjsLib && pdfjsLib.getDocument) {
          const loadingTask = pdfjsLib.getDocument(props.pdfUrl)
          loadingTask.promise.then(
            (pdf: any) => {
              if (!didCancel) {
                setPdfObj(pdf)
                setPageCount(pdf.numPages)
                if (scrollable) {
                  setDocLoading(false)
                } else {
                  setPageNum(1)
                  setDocLoading(false)
                }
              }
            },
            (reason: any) => {
              if (!didCancel) {
                console.error('PDF loading error:', reason)
                setDocLoading(false)
              }
            }
          )
        } else {
          setTimeout(checkPdfJsLib, 100)
        }
      }
      checkPdfJsLib()
    }
    return () => {
      didCancel = true
    }
  }, [libraryLoading, props.pdfUrl])

  useEffect(() => {
    if (pdfObj && pageCount) {
      if (scrollable) {
        renderAllPages()
      } else if (pageNum) {
        loadPage()
      }
    }
  }, [pdfObj, pageNum, pageCount, scrollable, windowWidth])

  const loadPage = () => {
    const pageNumber = pageNum
    setDocLoading(true)
    pdfObj.getPage(pageNumber).then((page: any) => {
      const scale = 1
      const viewport = page.getViewport({ scale })
      const canvas = document.getElementById('pdf-target') as HTMLCanvasElement
      if (canvas) {
        const context = canvas.getContext('2d')
        if (context) {
          canvas.height = viewport.height
          canvas.width = viewport.width
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          }
          const renderTask = page.render(renderContext)
          renderTask.promise.then(() => {
            setDocLoading(false)
          })
        } else {
          setDocLoading(false)
          console.error('Failed to get canvas context')
        }
      } else {
        setDocLoading(false)
        console.error('Canvas element not found')
      }
    })
  }

  const renderAllPages = async () => {
    if (!pdfObj || !pageCount) return

    setDocLoading(true)
    setRenderedPages(0)

    try {
      const devicePixelRatio = window.devicePixelRatio || 1

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdfObj.getPage(pageNum)
        const baseViewport = page.getViewport({ scale: 1 })

        const targetContainer = scrollable
          ? document.getElementById('pdf-pages-container')?.parentElement
          : document.getElementById('pdf-target')?.parentElement
        const availableWidth = (targetContainer?.getBoundingClientRect().width || 0) - 16

        const renderScale = Math.min(1, availableWidth / baseViewport.width)
        let scaledViewport = page.getViewport({ scale: renderScale })
        const canvas = document.createElement('canvas')
        canvas.id = `pdf-page-${pageNum}`

        // Canvas size in physical pixels (for high-DPI rendering)
        canvas.height = scaledViewport.height * devicePixelRatio
        canvas.width = scaledViewport.width * devicePixelRatio

        // CSS size in logical pixels (for display)
        canvas.style.height = `${scaledViewport.height}px`
        canvas.style.width = `${scaledViewport.width}px`

        const context = canvas.getContext('2d')
        if (context) {
          context.scale(devicePixelRatio, devicePixelRatio)

          const renderContext = {
            canvasContext: context,
            viewport: scaledViewport,
          }
          await page.render(renderContext).promise
        }

        const container = document.getElementById('pdf-pages-container')
        if (container) {
          container.appendChild(canvas)
        }

        setRenderedPages(pageNum)
      }
    } catch (error) {
      console.error('Error rendering PDF pages:', error)
    } finally {
      setDocLoading(false)
    }
  }

  const handleDownload = () => {
    try {
      const urlWithoutParams = props.pdfUrl.split('?')[0]
      const urlParts = urlWithoutParams.split('/')
      const filename = urlParts[urlParts.length - 1] || 'download.pdf'
      const finalFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
      downloadFile({
        file_contents: props.pdfUrl,
        title: finalFilename,
      } as PrivateFileType)
    } catch (error) {
      console.error('Error downloading PDF:', error)
    }
  }

  return (
    <PdfRendererWrapper scrollable={scrollable}>
      {allowDownload && !loading && (
        <DownloadButtonContainer>
          <IconButton
            size="medium"
            variant="outlined"
            color="default"
            aria-label="Download PDF"
            onClick={handleDownload}
          >
            <DownloadIcon />
          </IconButton>
        </DownloadButtonContainer>
      )}
      {scrollable ? (
        <PdfPagesContainer id="pdf-pages-container">
          {!scrollable &&
            renderedPages > 0 &&
            Array.from({ length: renderedPages }, (_, i) => <ScrollableCanvas key={i + 1} id={`pdf-page-${i + 1}`} />)}
        </PdfPagesContainer>
      ) : (
        <Canvas id="pdf-target" />
      )}
      {!loading && !scrollable && (
        <PageNavbar>
          <IconButton onClick={() => setPageNum(Math.max(1, pageNum - 1))} disabled={pageNum <= 1}>
            <ArrowLeftSmIcon />
          </IconButton>

          <span>{translate('Page %{pageNum} of %{pageCount}', { pageNum, pageCount })}</span>
          <IconButton
            onClick={() => setPageNum(Math.min(pageNum + 1, pageCount || pageNum))}
            disabled={pageNum >= (pageCount || 1)}
          >
            <ArrowRightSmIcon />
          </IconButton>
        </PageNavbar>
      )}
      {loading && <LoadingDots />}
    </PdfRendererWrapper>
  )
}
